// The controls strip above a matrix question: which view its sub-questions are charted in, which
// tops a box score reads, and the help text explaining the view on screen.

import { useMemo } from 'react'
import PropTypes from 'prop-types'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import { Dropdown } from 'primereact/dropdown'

import { CHART_VIEW_MAPPING, DEFAULT_CHART_TOPS } from '@aytm/app/stats/page/question-views/components/lib/constants'
import {
  getDefaultChartView,
  resolveAvailableViewModesByFormat
} from '@aytm/app/stats/page/question-views/components/controls/lib/view-modes'
import ControlsWithToggle from '@aytm/app/stats/page/question-views/components/controls/ControlsWithToggle'
import { getChartTopsMapping, getHelpText } from '@aytm/app/stats/page/question-views/mx/lib/utils'
import HelpText from '@aytm/app/stats/page/question-views/mx/components/HelpText'

const TYPE = 'mx'

// a matrix is the one type whose help text points at the slider-question article
const HELP_LINK = 'https://helpcenter.aytm.com/hc/en-us/articles/360049241851-Slider-Questions'

const topsValueTemplate = option => option?.text || option?.listText

const ControlsMX = ({
  format,
  batches,
  maxAnswerPosition,
  viewMode,
  topsMode,
  itemsCollapsed,
  onToggleBatches,
  onModelChange
}) => {
  const topsModeOptions = useMemo(
    () => getChartTopsMapping(TYPE, batches, maxAnswerPosition),
    [batches, maxAnswerPosition]
  )

  const options = useMemo(() => {
    const declared = CHART_VIEW_MAPPING[TYPE] || []
    const available = resolveAvailableViewModesByFormat(declared, format)

    // a format override that filters every view out leaves the type default as the sole option,
    // which collapses to the hidden single-option strip below
    if (!available.length && declared.length) {
      const fallback = getDefaultChartView(declared)

      return declared.filter(view => view.value === fallback)
    }

    // a box score needs tops to read; with none there is nothing for that view to show
    return topsModeOptions.length ? available : available.filter(view => 'boxScore' !== view?.value)
  }, [format, topsModeOptions])

  const helpText = getHelpText(viewMode, topsMode || DEFAULT_CHART_TOPS)

  // a single view is no choice to offer, so the strip stays out of the way
  if (null == options || 1 === options.length) return null

  const onViewModeChange = event => onModelChange({ viewMode: event.value, topsMode: DEFAULT_CHART_TOPS })

  return (
    <ControlsWithToggle type={TYPE} batches={batches} itemsCollapsed={itemsCollapsed} onToggleBatches={onToggleBatches}>
      <div className="p-fluid p-col-3">
        <Dropdown
          data-testid="stat-question-view-mode"
          className="p-dropdown-rounded"
          aria-label="View mode"
          ariaLabel="View mode"
          value={viewMode || getDefaultChartView(options)}
          options={options}
          optionValue="value"
          optionLabel="text"
          optionDisabled="disabled"
          fixedDropdownDir="bottom"
          onChange={onViewModeChange}
        />
      </div>
      {'boxScore' === viewMode ? (
        <div className="p-fluid p-col-3">
          <Dropdown
            data-testid="stat-question-tops-mode"
            className="p-dropdown-rounded"
            aria-label="Top box"
            ariaLabel="Top box"
            value={topsMode || DEFAULT_CHART_TOPS}
            options={topsModeOptions}
            optionValue="value"
            optionLabel="listText"
            optionDisabled="disabled"
            fixedDropdownDir="bottom"
            valueTemplate={topsValueTemplate}
            onChange={event => onModelChange({ topsMode: event.value })}
          />
        </div>
      ) : null}
      {helpText ? (
        <Popup
          width="300px"
          trigger="click"
          direction="righttop"
          popupOffset={9}
          hideOtherPopupsOnShow
          content={() => (
            <>
              <HelpText text={helpText} />
              <br />
              <br />
              <a href={HELP_LINK} target="_blank" rel="noreferrer">
                Learn More
              </a>
            </>
          )}
        >
          <i className="icon-aytmfd-help-v2" />
        </Popup>
      ) : null}
    </ControlsWithToggle>
  )
}

ControlsMX.propTypes = {
  format: PropTypes.object,
  batches: PropTypes.array,
  maxAnswerPosition: PropTypes.number,
  viewMode: PropTypes.string,
  topsMode: PropTypes.string,
  itemsCollapsed: PropTypes.bool,
  onToggleBatches: PropTypes.func,
  onModelChange: PropTypes.func
}

ControlsMX.displayName = 'ControlsMX'

export default ControlsMX
